import React from 'react';
import { etDaysToExpiry, isAwaitingSettlement, isExpiringSoon } from '../../utils/expiry';
import { Briefcase, History, TrendingUp, TrendingDown, CheckCircle, XCircle, AlertCircle, X } from 'lucide-react';
import { Position, ClosedTrade } from '../../types/position';
import { pnlColor60 } from '../../utils/pnl';
import { formatDateShort } from '../../utils/format';

interface PositionsPanelProps {
  openPositions: Position[];
  closedTrades: ClosedTrade[];
  onViewPosition: (position: Position) => void;
  onClosePosition: (position: Position) => void;
}

const PositionsPanel: React.FC<PositionsPanelProps> = ({
  openPositions,
  closedTrades,
  onViewPosition,
  onClosePosition
}) => {
  // Normalize positions - handle both direct Position objects and wrapped {position: {...}, success: true} format
  const normalizedPositions: Position[] = (openPositions || []).map((item: any) => {
    // If item has a 'position' property with the actual data, unwrap it
    if (item && item.position && item.position.symbol) {
      return item.position as Position;
    }
    // Otherwise assume it's already a Position object
    return item as Position;
  }).filter((p: Position) => p && p.symbol && p.strike !== undefined);
  
  const totalOpenPnl = normalizedPositions.reduce((sum, p) => sum + (p.unrealized_pnl || 0), 0);

  return (
    <div className="flex flex-col h-full">
      {/* Opened Positions */}
      <div className="flex-1 overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-3 py-2.5 border-b border-slate-700/50">
          <div className="flex items-center gap-2">
            <Briefcase className="w-4 h-4 text-blue-400" />
            <h2 className="text-sm font-semibold text-white">Open Positions</h2>
          </div>
          <div className={`text-xs font-semibold px-2 py-0.5 rounded ${
            totalOpenPnl >= 0 
              ? 'bg-green-500/10 text-green-400' 
              : 'bg-red-500/10 text-red-400'
          }`}>
            {totalOpenPnl >= 0 ? '+' : ''}${totalOpenPnl.toFixed(0)}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-2 space-y-2">
          {normalizedPositions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-6 text-slate-500">
              <Briefcase className="w-6 h-6 mb-2 opacity-50" />
              <p className="text-xs">No open positions</p>
            </div>
          ) : (
            normalizedPositions.map((position) => {
              const pnl = position.unrealized_pnl || 0;
              const pnlPct = position.unrealized_roi_pct ?? position.unrealized_pnl_pct ?? 0;
              const isProfit = pnl >= 0;
              const hasStockLeg = !!(position.stock_entry_price && position.current_stock_price && position.strategy?.includes('Call'));
              const daysToExpiry = etDaysToExpiry(position.expiration);
              // An option whose expiration session has passed is not "-1d left": it has expired
              // and is waiting to settle on that session's official close at the next exit cycle.
              const isSettling = isAwaitingSettlement(position.strategy, daysToExpiry);
              const isUrgent = isExpiringSoon(position.strategy, daysToExpiry);

              return (
                <div
                  key={position.id}
                  className={`rounded-lg overflow-hidden border ${
                    isProfit
                      ? 'bg-green-500/5 border-green-500/20'
                      : 'bg-red-500/5 border-red-500/20'
                  }`}
                >
                  {/* Header Row */}
                  <div 
                    onClick={() => onViewPosition(position)}
                    className="px-2.5 py-2 cursor-pointer hover:bg-slate-800/30 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-bold text-white">{position.symbol}</span>
                        <span className="text-slate-500">•</span>
                        <span className="text-10 text-slate-400">{position.strategy}</span>
                      </div>
                      {isSettling && (
                        <span
                          title="Expired — settles at the next exit cycle on the expiration session's official close"
                          className="text-10 md:text-9 px-1.5 py-0.5 rounded bg-slate-500/20 text-slate-300"
                        >
                          expired · settling
                        </span>
                      )}
                      {isUrgent && (
                        <span className="text-10 md:text-9 px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-400">
                          {daysToExpiry}d left
                        </span>
                      )}
                    </div>

                    {/* Compact metrics row */}
                    <div className="flex items-center justify-between text-10 text-slate-400 mb-1.5">
                      {position.strategy === 'SHARES'
                        ? <span>{position.contracts * 100} shares @ ${(position.stock_entry_price || 0).toFixed(2)}</span>
                        : <span>${position.strike} @ ${(position.entry_premium || 0).toFixed(2)}</span>
                      }
                      <span>Exp: {position.strategy === 'SHARES' ? 'N/A' : formatDateShort(position.expiration)}</span>
                    </div>

                    {/* P&L */}
                    <div className="flex items-center gap-1.5">
                      {isProfit ? (
                        <TrendingUp className="w-3 h-3 text-green-400" />
                      ) : (
                        <TrendingDown className="w-3 h-3 text-red-400" />
                      )}
                      <span className={`text-sm font-bold ${isProfit ? 'text-green-400' : 'text-red-400'}`}>
                        {isProfit ? '+' : ''}${(pnl || 0).toFixed(0)}
                      </span>
                      <span className={`text-10 ${isProfit ? 'text-green-400' : 'text-red-400'}`}>
                        ({pnlPct >= 0 ? '+' : ''}{(pnlPct || 0).toFixed(0)}%)
                      </span>
                    </div>
                    {/* CC: show option/stock split */}
                    {hasStockLeg && position.option_pnl != null && (
                      <div className="flex items-center gap-2 text-10 md:text-9 mt-0.5">
                        <span className={pnlColor60(position.option_pnl ?? 0)}>
                          Opt: {(position.option_pnl ?? 0) >= 0 ? '+' : ''}${(position.option_pnl ?? 0).toFixed(0)}
                        </span>
                        <span className={pnlColor60(position.stock_pnl ?? 0)}>
                          Stk: {(position.stock_pnl ?? 0) >= 0 ? '+' : ''}${(position.stock_pnl ?? 0).toFixed(0)}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Close Position Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onClosePosition(position);
                    }}
                    className="w-full py-1.5 bg-slate-800/80 hover:bg-red-500/20 border-t border-slate-700/40 text-10 font-medium text-slate-400 hover:text-red-400 transition-colors flex items-center justify-center gap-1"
                  >
                    <X className="w-3 h-3" />
                    Close Position
                  </button>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Recent Closed */}
      <div className="border-t border-slate-700/50">
        <div className="flex items-center justify-between px-3 py-2 border-b border-slate-700/50">
          <div className="flex items-center gap-2">
            <History className="w-4 h-4 text-slate-400" />
            <h2 className="text-sm font-semibold text-white">Recent Closed</h2>
          </div>
          <span className="text-10 text-slate-500">{closedTrades.length}</span>
        </div>

        <div className="p-2 space-y-1.5 max-h-36 overflow-y-auto">
          {closedTrades.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-3 text-slate-500">
              <History className="w-5 h-5 mb-1.5 opacity-50" />
              <p className="text-10">No closed trades</p>
            </div>
          ) : (
            closedTrades.map((trade) => {
              const isProfit = trade.outcome === 'profit';
              const isLoss = trade.outcome === 'loss';

              return (
                <div
                  key={trade.id}
                  className="rounded-md bg-slate-800/30 border border-slate-700/30 overflow-hidden"
                >
                  <div className="flex items-center justify-between px-2 py-1.5">
                    <div className="flex items-center gap-1.5">
                      {isProfit && <CheckCircle className="w-3 h-3 text-green-400" />}
                      {isLoss && <XCircle className="w-3 h-3 text-red-400" />}
                      {!isProfit && !isLoss && <AlertCircle className="w-3 h-3 text-slate-400" />}
                      <div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs font-semibold text-white">{trade.symbol}</span>
                          <span className="text-10 md:text-9 text-slate-500">{trade.strategy}</span>
                        </div>
                        <span className="text-10 md:text-9 text-slate-500">{formatDateShort(trade.closeDate)}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`text-xs font-semibold block ${
                        isProfit ? 'text-green-400' : isLoss ? 'text-red-400' : 'text-slate-400'
                      }`}>
                        {(trade.pnl || 0) >= 0 ? '+' : ''}${(trade.pnl || 0).toFixed(0)}
                      </span>
                      <span className={`text-10 md:text-9 ${
                        isProfit ? 'text-green-400/70' : isLoss ? 'text-red-400/70' : 'text-slate-400/70'
                      }`}>
                        {(trade.pnlPercent || 0) >= 0 ? '+' : ''}{(trade.pnlPercent || 0).toFixed(0)}%
                      </span>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default PositionsPanel;
